import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, Pencil, RefreshCw, FileDown, ArrowRightCircle, CheckCircle2, Copy, Wrench, Trash2, Undo2 } from "lucide-react";
import api, { apiErrorMessage, API_BASE } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ProductThumb } from "@/components/common/ProductThumb";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function QuoteDetail() {
  const { id } = useParams();
  const { t, fmtCurrency, fmtDate } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [marginGuard, setMarginGuard] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [manualAcceptance, setManualAcceptance] = useState({ accepted_by_name: "", channel: "email", evidence_note: "" });

  const { data: q, isLoading, refetch } = useQuery({ queryKey: ["quote", id], queryFn: async () => (await api.get(`/quotes/${id}`)).data });

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const action = async (fn, successMsg, after) => {
    setBusy(true);
    try { const res = await fn(); toast.success(successMsg); qc.invalidateQueries({ queryKey: ["quotes"] }); if (after) after(res); else refetch(); }
    catch (err) { toast.error(apiErrorMessage(err, t)); }
    finally { setBusy(false); }
  };

  const publicUrl = q.public_token ? `${window.location.origin}/q/${q.public_token}` : null;

  const preSend = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/quotes/${q.id}/margin`); setMarginGuard(data.available && data.low ? data : null); }
    catch { setMarginGuard(null); }
    finally { setBusy(false); setSendOpen(true); }
  };
  const doSend = () => {
    setSendOpen(false);
    action(() => api.post(`/quotes/${q.id}/send`, reason ? { override_reason: reason } : {}), t("quotes.sent_success"));
    setReason(""); setMarginGuard(null);
  };
  const doManualAccept = () => {
    action(
      () => api.post(`/quotes/${q.id}/accept`, manualAcceptance),
      t("quotes.manual_accept_success"),
      () => {
        setManualOpen(false);
        setManualAcceptance({ accepted_by_name: "", channel: "email", evidence_note: "" });
        refetch();
      },
    );
  };
  const doConvert = (target) => {
    setConvertOpen(false);
    action(
      () => api.post(`/quotes/${q.id}/convert`, { target }),
      t(target === "service" ? "quotes.converted_service_success" : "quotes.converted_success"),
      (res) => navigate(target === "service" ? `/jobs?open=${res.data.id}` : `/orders/${res.data.id}`),
    );
  };
  const doDelete = () => {
    setDeleteOpen(false);
    action(() => api.delete(`/quotes/${q.id}`), t("quotes.deleted_success"), () => navigate("/quotes"));
  };
  const doReverse = () => {
    setReverseOpen(false);
    action(() => api.post(`/quotes/${q.id}/reverse-conversion`), t("quotes.reverse_success"));
  };
  const openLatestRevision = () => {
    if (!q.latest_revision_id) return;
    if (q.latest_revision_status === "draft") {
      navigate(`/quotes/${q.latest_revision_id}/edit`);
    } else {
      navigate(`/quotes/${q.latest_revision_id}`);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  return (
    <div>
      <Breadcrumbs items={[{ label: t("quotes.title"), to: "/quotes" }, { label: q.quote_number }]} />
      <PageHeader
        title={`${q.quote_number}`}
        subtitle={<span className="flex items-center gap-2">{q.customer_name} · v{q.version} <StatusBadge status={q.status} /></span>}
        actions={<Button variant="outline" onClick={() => navigate("/quotes")} data-testid="quote-detail-back"><ArrowLeft className="h-4 w-4" />{t("common.back")}</Button>}
      />

      <div className="mb-4 flex flex-wrap gap-2" data-testid="quote-actions">
        <Button variant="outline" onClick={downloadPdf} data-testid="quote-pdf-button"><FileDown className="h-4 w-4" />{t("quotes.download_pdf")}</Button>

        {q.status === "draft" && can("quotes:write") && (
          <Button variant="outline" onClick={() => navigate(`/quotes/${q.id}/edit`)} data-testid="quote-edit-button"><Pencil className="h-4 w-4" />{t("common.edit")}</Button>
        )}

        {q.status === "draft" && can("quotes:send") && (
          <Button disabled={busy} onClick={preSend} data-testid="quote-send-button"><Send className="h-4 w-4" />{t("quotes.send")}</Button>
        )}

        {["sent", "accepted", "rejected", "expired"].includes(q.status) && !q.converted_order_id && !q.converted_job_id && can("quotes:write") && (
          <Button variant="outline" disabled={busy} onClick={() => action(() => api.post(`/quotes/${q.id}/revise`), t("quotes.revised_success"), (res) => navigate(`/quotes/${res.data.id}/edit`))} data-testid="quote-revise-button"><RefreshCw className="h-4 w-4" />{q.status === "accepted" ? t("quotes.revise") : t("common.edit")}</Button>
        )}

        {((!["accepted", "revised"].includes(q.status) && !q.converted_order_id && !q.converted_job_id) || (q.status === "revised" && q.revision_chain_deletable)) && can("quotes:delete") && (
          <Button variant="destructive" disabled={busy} onClick={() => setDeleteOpen(true)} data-testid="quote-delete-button"><Trash2 className="h-4 w-4" />{t("common.delete")}</Button>
        )}

        {q.status === "revised" && q.latest_revision_id !== q.id && (
          <Button onClick={openLatestRevision} data-testid="quote-open-latest"><ArrowRightCircle className="h-4 w-4" />{t("quotes.open_current_version")}</Button>
        )}

        {q.status === "sent" && can("quotes:write") && (
          <Button variant="outline" disabled={busy} onClick={() => setManualOpen(true)} data-testid="quote-manual-accept-button">
            <CheckCircle2 className="h-4 w-4" />{t("quotes.manual_accept")}
          </Button>
        )}

        {q.status === "accepted" && !q.converted_order_id && !q.converted_job_id && can("quotes:convert") && (
          <Button disabled={busy} onClick={() => setConvertOpen(true)} data-testid="quote-convert-button"><ArrowRightCircle className="h-4 w-4" />{t(q.vehicle_id ? "quotes.convert_service" : "quotes.convert")}</Button>
        )}

        {q.converted_order_id && (
          <Button variant="secondary" onClick={() => navigate(`/orders/${q.converted_order_id}`)} data-testid="quote-view-order"><ArrowRightCircle className="h-4 w-4" />{t("quotes.converted_to")}</Button>
        )}
        {q.converted_job_id && (
          <Button variant="secondary" onClick={() => navigate(`/jobs?open=${q.converted_job_id}`)} data-testid="quote-view-job"><Wrench className="h-4 w-4" />{t("quotes.converted_to_service")}</Button>
        )}
        {(q.converted_order_id || q.converted_job_id) && can("quotes:convert") && (
          <Button variant="outline" disabled={busy} onClick={() => setReverseOpen(true)} data-testid="quote-reverse-conversion"><Undo2 className="h-4 w-4" />{t("quotes.reverse_conversion")}</Button>
        )}
      </div>

      {q.status === "revised" && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="quote-revised-explanation">
          <div className="font-medium">{t("quotes.revised_explanation_title")}</div>
          <div>{t("quotes.revised_explanation_desc", { number: q.latest_revision_number })}</div>
        </div>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t("quotes.delete_title")}</AlertDialogTitle><AlertDialogDescription>{t("quotes.delete_desc")}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel><AlertDialogAction className="bg-red-600 text-white hover:bg-red-700" onClick={doDelete}>{t("common.delete")}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reverseOpen} onOpenChange={setReverseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t("quotes.reverse_title")}</AlertDialogTitle><AlertDialogDescription>{t("quotes.reverse_desc")}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel><AlertDialogAction onClick={doReverse}>{t("quotes.reverse_confirm")}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent data-testid="quote-convert-dialog">
          <DialogHeader><DialogTitle>{t("quotes.choose_conversion_title")}</DialogTitle><DialogDescription>{t(q.vehicle_id ? "quotes.choose_conversion_automotive_desc" : "quotes.choose_conversion_desc")}</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <Button className="h-auto justify-start p-4 text-left" variant={q.vehicle_id ? "default" : "outline"} onClick={() => doConvert("service")} data-testid="convert-to-service"><Wrench className="mr-3 h-5 w-5" /><span><span className="block font-semibold">{t("quotes.convert_service")}</span><span className="block text-xs font-normal opacity-80">{t("quotes.convert_service_desc")}</span></span></Button>
            <Button className="h-auto justify-start p-4 text-left" variant={q.vehicle_id ? "outline" : "default"} onClick={() => doConvert("order")} data-testid="convert-to-order"><ArrowRightCircle className="mr-3 h-5 w-5" /><span><span className="block font-semibold">{t("quotes.convert_order")}</span><span className="block text-xs font-normal opacity-80">{t("quotes.convert_order_desc")}</span></span></Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setConvertOpen(false)}>{t("common.cancel")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={sendOpen} onOpenChange={setSendOpen}>
        <AlertDialogContent data-testid="send-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{marginGuard ? t("quotes.margin_guard_title") : t("quotes.confirm_send_title")}</AlertDialogTitle>
            <AlertDialogDescription>{marginGuard ? t("quotes.margin_guard_desc", { pct: marginGuard.target }) : t("quotes.confirm_send_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          {marginGuard && (
            <div className="space-y-2">
              <div className="max-h-40 space-y-1 overflow-auto text-sm">
                {marginGuard.low_lines.map((l, i) => (
                  <div key={i} className="flex justify-between rounded bg-red-50 px-3 py-1.5 dark:bg-red-950/30" data-testid="send-low-line">
                    <span className="truncate">{l.description}</span><span className="tabular-nums font-medium text-red-600">{l.margin_pct}%</span>
                  </div>
                ))}
              </div>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder={t("quotes.margin_reason_ph")} aria-label={t("quotes.margin_reason")} data-testid="send-margin-reason" />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="send-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doSend} data-testid="confirm-send">{marginGuard ? t("quotes.send_anyway") : t("quotes.send")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-accept-dialog">
          <DialogHeader>
            <DialogTitle>{t("quotes.manual_accept_title")}</DialogTitle>
            <DialogDescription>{t("quotes.manual_accept_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="manual-accept-name">{t("quotes.manual_accept_name")}</Label>
              <Input id="manual-accept-name" value={manualAcceptance.accepted_by_name}
                onChange={(e) => setManualAcceptance((x) => ({ ...x, accepted_by_name: e.target.value }))}
                data-testid="manual-accept-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-accept-channel">{t("quotes.manual_accept_channel")}</Label>
              <select id="manual-accept-channel" value={manualAcceptance.channel}
                onChange={(e) => setManualAcceptance((x) => ({ ...x, channel: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                data-testid="manual-accept-channel">
                <option value="email">{t("quotes.channel_email")}</option>
                <option value="phone">{t("quotes.channel_phone")}</option>
                <option value="in_person">{t("quotes.channel_in_person")}</option>
                <option value="insurance">{t("quotes.channel_insurance")}</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-accept-note">{t("quotes.manual_accept_note")}</Label>
              <Textarea id="manual-accept-note" rows={3} value={manualAcceptance.evidence_note}
                onChange={(e) => setManualAcceptance((x) => ({ ...x, evidence_note: e.target.value }))}
                placeholder={t("quotes.manual_accept_note_ph")} data-testid="manual-accept-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={doManualAccept} disabled={busy || manualAcceptance.accepted_by_name.trim().length < 2 || manualAcceptance.evidence_note.trim().length < 3}
              data-testid="manual-accept-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("quotes.manual_accept_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {q.status === "accepted" && (
        <div className="mb-4 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" data-testid="acceptance-evidence">
          <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4" />
            {t("quotes.acceptance_info", { name: q.accepted_by_name || "—", date: fmtDate(q.accepted_at), version: q.accepted_version })}</div>
        </div>
      )}

      {publicUrl && ["sent", "accepted", "rejected"].includes(q.status) && (
        <Card className="mb-4 p-4" data-testid="acceptance-link-card">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("quotes.acceptance_link")}</Label>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-slate-100 px-3 py-2 font-mono text-xs dark:bg-slate-800" data-testid="acceptance-link-value">{publicUrl}</code>
            <Button variant="outline" size="sm" onClick={async () => { try { await navigator.clipboard.writeText(publicUrl); } catch { const t0 = document.createElement("textarea"); t0.value = publicUrl; document.body.appendChild(t0); t0.select(); try { document.execCommand("copy"); } catch { /* ignore */ } document.body.removeChild(t0); } toast.success(t("quotes.link_copied")); }} data-testid="copy-link-button"><Copy className="h-4 w-4" />{t("quotes.copy_link")}</Button>
          </div>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14"></TableHead>
              <TableHead>{t("quotes.description")}</TableHead>
              <TableHead className="text-right">{t("quotes.quantity")}</TableHead>
              <TableHead className="text-right">{t("quotes.unit_price")}</TableHead>
              <TableHead className="text-right">{t("quotes.discount")}</TableHead>
              <TableHead className="text-right">{t("quotes.vat")}</TableHead>
              <TableHead className="text-right">{t("quotes.line_net")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.line_items.map((l) => (
              <TableRow key={l.id} data-testid={`detail-line-${l.id}`} className={l.is_optional ? "opacity-60" : ""}>
                <TableCell><ProductThumb path={l.image_url} alt={l.description} size={40} /></TableCell>
                <TableCell className="font-medium">{l.description}{l.is_optional && <span className="ml-2 text-xs text-slate-400">({t("common.optional")})</span>}</TableCell>
                <TableCell className="text-right tabular-nums">{l.quantity} {l.unit}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(l.unit_price)}</TableCell>
                <TableCell className="text-right tabular-nums">{l.discount_percent}%</TableCell>
                <TableCell className="text-right tabular-nums">{l.vat_rate}%</TableCell>
                <TableCell className="text-right tabular-nums font-medium">{fmtCurrency(l.line_net)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex justify-end border-t border-slate-200 p-4 dark:border-slate-800">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">{t("quotes.subtotal")}</span><span className="tabular-nums" data-testid="detail-subtotal">{fmtCurrency(q.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("quotes.discount_total")}</span><span className="tabular-nums">{fmtCurrency(q.discount_total)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("quotes.tax_total")}</span><span className="tabular-nums">{fmtCurrency(q.tax_total)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1 font-heading text-base font-bold dark:border-slate-700"><span>{t("quotes.total")}</span><span className="tabular-nums" data-testid="detail-total">{fmtCurrency(q.total)}</span></div>
          </div>
        </div>
      </Card>

      {(q.notes || q.terms) && (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {q.notes && <Card className="p-4"><Label className="text-xs uppercase text-slate-500">{t("common.notes")}</Label><p className="mt-1 text-sm">{q.notes}</p></Card>}
          {q.terms && <Card className="p-4"><Label className="text-xs uppercase text-slate-500">{t("quotes.terms")}</Label><p className="mt-1 text-sm">{q.terms}</p></Card>}
        </div>
      )}
    </div>
  );
}
